import type { QaReportCategoryKey } from "./qaReportTypes";

export const QA_REPORT_IDS = {
  receivingDetails: 1,
  receivingSummary: 2,
  reorderLimitItems: 3,
  salesByPlu: 4,
  deletedCartons: 5,
  inventoryValuation: 6,
  openWips: 7,
  inventoryFlow: 8,
  productionDetail: 9,
  productionSummary: 10,
  salesComparison: 11,
  employeeWeighing: 12,
  customerProfitability: 13,
  productionCostYield: 14,
  financialSnapshot: 15,
  financeDataQuality: 16,
  fieldCosts: 17,
} as const;

export type QaReportId = (typeof QA_REPORT_IDS)[keyof typeof QA_REPORT_IDS];

export type QaReportOptionData =
  | "classes"
  | "customers"
  | "products"
  | "rooms"
  | "suppliers"
  | "workOrders"
  | "weighingUsers";

export type QaReportLabels = {
  wip: string;
  wips: string;
  room: string;
  rooms: string;
  productionCostYieldReport: string;
};

const DEFAULT_REPORT_LABELS: QaReportLabels = {
  wip: "WIP",
  wips: "WIPs",
  room: "Room",
  rooms: "Rooms",
  productionCostYieldReport: "Production Cost & Yield",
};

type DynamicReportCopy = string | ((labels: QaReportLabels) => string);

type QaReportDefinition = {
  id: QaReportId;
  category: QaReportCategoryKey;
  categoryOrder: number;
  endpoint: string;
  label: DynamicReportCopy;
  description: DynamicReportCopy;
  shows: DynamicReportCopy;
  optionData: QaReportOptionData[];
  requiresDateRange?: boolean;
  serializeDates?: boolean;
  useStartDateAsEndDate?: boolean;
};

export type QaReportOption = {
  value: QaReportId;
  label: string;
  description: string;
  shows: string;
};

type QaReportVisibility = {
  includeFieldCosts?: boolean;
};

export type QaReportSelection =
  | QaReportOption
  | {
      value: -1;
      label: string;
    };

export const QA_REPORT_DEFINITIONS: readonly QaReportDefinition[] = [
  {
    id: QA_REPORT_IDS.deletedCartons,
    category: "audit",
    categoryOrder: 1,
    endpoint: "deletedCartonsReport",
    label: "Deleted Cartons",
    description:
      "Review inventory removals to understand what was deleted, why it was removed, and who performed the action.",
    shows:
      "Action, reason, date, barcode, operator, carton count, net weight, and piece count.",
    optionData: [],
  },
  {
    id: QA_REPORT_IDS.inventoryValuation,
    category: "inventory",
    categoryOrder: 2,
    endpoint: "inventoryValuationReport",
    label: "Inventory Valuation",
    description:
      "Understand the quantity and estimated value of inventory held on the selected date.",
    shows:
      "Product, cartons, weight, pieces, average cost, and total inventory cost.",
    optionData: [],
    useStartDateAsEndDate: true,
  },
  {
    id: QA_REPORT_IDS.inventoryFlow,
    category: "inventory",
    categoryOrder: 4,
    endpoint: "inventoryFlow",
    label: "Flow Reports",
    description:
      "Follow a product through receiving, production, inventory movement, and shipment for operational traceability.",
    shows:
      "Customer or supplier, lot number, destination, date, movement type, and quantity.",
    optionData: ["products"],
  },
  {
    id: QA_REPORT_IDS.openWips,
    category: "inventory",
    categoryOrder: 3,
    endpoint: "openWipsReport",
    label: ({ wips }) => `Open ${wips} Report`,
    description: ({ wip }) =>
      `See active ${wip.toLowerCase()} work and compare material going in with production coming out.`,
    shows: ({ wip, room }) =>
      `${room}, ${wip.toLowerCase()} number, incoming and outgoing quantities, input cost, and output value.`,
    optionData: [],
  },
  {
    id: QA_REPORT_IDS.productionDetail,
    category: "operations",
    categoryOrder: 3,
    endpoint: "productionDetailsReport",
    label: "Production Detail",
    description:
      "Inspect production at the lot level to verify when specific products were made and how much was produced.",
    shows:
      "PLU, production date, lot number, best-before date, cartons, weight, and pieces.",
    optionData: ["products"],
    requiresDateRange: true,
  },
  {
    id: QA_REPORT_IDS.productionSummary,
    category: "operations",
    categoryOrder: 4,
    endpoint: "productionSummaryReport",
    label: "Production Summary",
    description:
      "Review total production output by product to understand throughput over the selected period.",
    shows:
      "PLU, product name, pallets, cartons, total weight, total pieces, and recorded output value.",
    optionData: ["classes", "products"],
    requiresDateRange: true,
  },
  {
    id: QA_REPORT_IDS.receivingDetails,
    category: "operations",
    categoryOrder: 1,
    endpoint: "generateGeneralReceivingDetailsReport",
    label: "Receiving Details",
    description:
      "Audit item-level receiving activity and verify inbound quantities and costs from suppliers.",
    shows:
      "PLU, product, unit cost, received weight or pieces, and total cost for each receiving.",
    optionData: ["classes", "products", "suppliers"],
  },
  {
    id: QA_REPORT_IDS.receivingSummary,
    category: "operations",
    categoryOrder: 2,
    endpoint: "generateGeneralReceivingSummaryReport",
    label: "Receiving Summary",
    description:
      "Compare inbound activity by supplier and invoice without reviewing every received carton.",
    shows:
      "Supplier, invoice, receiving date, total weight, pieces, and total cost.",
    optionData: ["classes", "products", "suppliers"],
  },
  {
    id: QA_REPORT_IDS.reorderLimitItems,
    category: "inventory",
    categoryOrder: 1,
    endpoint: "reorderLimitReport",
    label: "Reorder Limit Items",
    description:
      "Identify products that need replenishment before inventory shortages affect operations.",
    shows:
      "PLU, product, class or category, reorder limit, and current inventory in stock.",
    optionData: [],
  },
  {
    id: QA_REPORT_IDS.salesComparison,
    category: "sales",
    categoryOrder: 2,
    endpoint: "salesComparisonReport",
    label: "Sales Comparison Report",
    description:
      "Compare two sales periods to see whether volume, revenue, and profitability improved or declined.",
    shows:
      "Quantity sold, net sales, profit, margin, and period-over-period changes.",
    optionData: ["classes"],
  },
  {
    id: QA_REPORT_IDS.salesByPlu,
    category: "sales",
    categoryOrder: 1,
    endpoint: "salesReportByPlu",
    label: "Sales Report by PLU",
    description:
      "Understand which products were sold, to which customers, and the value of those sales.",
    shows:
      "Invoice, product, customer, cartons, weight, pieces, unit value, and total value.",
    optionData: ["classes", "products"],
  },
  {
    id: QA_REPORT_IDS.employeeWeighing,
    category: "operations",
    categoryOrder: 5,
    endpoint: "employeeWeighingReport",
    label: "Employee Weighing Report",
    description:
      "Review the production labels created by a weighing user for accountability and operator-level traceability.",
    shows:
      "Date, lot, PLU, product, barcode, net weight in kg and lbs, and pieces.",
    optionData: ["weighingUsers"],
  },
  {
    id: QA_REPORT_IDS.customerProfitability,
    category: "finance",
    categoryOrder: 1,
    endpoint: "finance/reports/customer-profitability",
    label: "Customer Profitability",
    description:
      "Compare customer revenue and profitability while accounting for product costs and credits.",
    shows:
      "Customer, shipped sales, credits, net sales, COGS, gross profit, margin, and costing status.",
    optionData: ["classes", "customers", "products"],
    requiresDateRange: true,
    serializeDates: true,
  },
  {
    id: QA_REPORT_IDS.productionCostYield,
    category: "finance",
    categoryOrder: 2,
    endpoint: "finance/reports/production-cost-yield",
    label: ({ productionCostYieldReport }) => productionCostYieldReport,
    description: ({ wip }) =>
      `Review production input costs, output quantities, unit costs, and yield by ${wip.toLowerCase()}.`,
    shows: ({ wip, room }) =>
      `${wip}, ${room.toLowerCase()}, input and output quantities, material cost, cost per output unit, yield, and costing status.`,
    optionData: ["classes", "products", "rooms"],
    requiresDateRange: true,
    serializeDates: true,
  },
  {
    id: QA_REPORT_IDS.fieldCosts,
    category: "finance",
    categoryOrder: 3,
    endpoint: "finance/reports/field-costs",
    label: ({ room }) => `${room} Costs`,
    description: ({ wips, room }) =>
      `Compare total costs and harvest results by ${room.toLowerCase()}, including the ${wips.toLowerCase()} opened there.`,
    shows: ({ wip, room }) =>
      `${room}, ${wip.toLowerCase()} count, inputs, harvest output, accumulated cost, average cost, yield, and status.`,
    optionData: ["rooms", "workOrders"],
    requiresDateRange: true,
    serializeDates: true,
  },
  {
    id: QA_REPORT_IDS.financialSnapshot,
    category: "finance",
    categoryOrder: 4,
    endpoint: "finance/reports/financial-snapshot",
    label: "Financial Snapshot",
    description: "Download a concise financial overview for the selected period.",
    shows:
      "Sales, COGS, gross profit, margin, backlog, receiving spend, production cost, inventory value, and leading products and customers.",
    optionData: [],
    requiresDateRange: true,
    serializeDates: true,
  },
  {
    id: QA_REPORT_IDS.financeDataQuality,
    category: "finance",
    categoryOrder: 5,
    endpoint: "finance/reports/data-quality",
    label: "Finance Data Quality",
    description:
      "Find missing source information before relying on financial totals and profitability.",
    shows:
      "Critical issues, warnings, affected records, unreliable values, and the reports impacted.",
    optionData: ["classes", "products"],
    requiresDateRange: true,
    serializeDates: true,
  },
];

export function getQaReportDefinition(
  reportId: number,
): QaReportDefinition | undefined {
  return QA_REPORT_DEFINITIONS.find(({ id }) => id === reportId);
}

export function getMissingQaReportOptionData(
  reportId: number,
  loadedOptionData: ReadonlySet<QaReportOptionData>,
): QaReportOptionData[] {
  return (getQaReportDefinition(reportId)?.optionData ?? []).filter(
    (optionData) => !loadedOptionData.has(optionData),
  );
}

export function buildQaReportOptions(
  labels: Partial<QaReportLabels> = {},
  visibility: QaReportVisibility = {},
): QaReportOption[] {
  const resolvedLabels = { ...DEFAULT_REPORT_LABELS, ...labels };
  const resolve = (copy: DynamicReportCopy) =>
    typeof copy === "function" ? copy(resolvedLabels) : copy;

  return QA_REPORT_DEFINITIONS.filter(
    ({ id }) => id !== QA_REPORT_IDS.fieldCosts || visibility.includeFieldCosts,
  ).map((definition) => ({
    value: definition.id,
    label: resolve(definition.label),
    description: resolve(definition.description),
    shows: resolve(definition.shows),
  }));
}
